import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { directSaleAPI } from '../../services/api';
import { Modal, Button, Pagination } from '../common';
import { fmtPKR, fmtDate } from '../../utils/formatters';
import type { DSOverdueItem } from '../../types';

// Module-level flag so the popup shows only once per app load (not on every navigation)
let alreadyShown = false;

export default function OverdueCreditAlert() {
  const [items, setItems] = useState<DSOverdueItem[]>([]);
  // Server totals cover EVERY overdue sale; the table lists at most the most-overdue page.
  const [summary, setSummary] = useState({ count: 0, outstanding: 0, truncated: false });
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState({ page: 1, pages: 0, total: 0 });
  const load = (p: number) => directSaleAPI.getOverdue(p).then(r => {
    setItems(r.data.data);
    if (r.data.pagination) setPage(r.data.pagination);
    return r;
  });

  useEffect(() => {
    if (alreadyShown) return;
    alreadyShown = true;
    load(1)
      .then(r => {
        const rows = r.data.data;
        if (rows && rows.length) {
          setSummary({ count: r.data.total ?? rows.length,
            outstanding: r.data.totalOutstanding ?? rows.reduce((s, i) => s + i.outstanding, 0), truncated: !!r.data.truncated });
          setOpen(true);
        }
      })
      .catch(() => { /* silent */ });
  }, []);

  if (!open) return null;

  return (
    <Modal open={open} onClose={() => setOpen(false)} title="⚠ Overdue Credit — Direct Sales" size="lg"
      footer={<Button onClick={() => setOpen(false)}>Got it</Button>}>
      <div className="space-y-3">
        <p className="text-sm text-ink-3">
          <b className="text-red-600">{summary.count}</b> direct sale(s) on credit are past their due date,
          with <b className="text-red-600">{fmtPKR(summary.outstanding)}</b> outstanding.
        </p>
        {summary.truncated && <p className="text-xs text-ink-4">Most overdue first, 10 per page.</p>}
        <div className="border border-red-200 rounded-sm overflow-hidden" style={{ borderRadius: 'var(--r-xs)' }}>
          <table className="w-full text-sm">
            <thead><tr style={{ background: '#FEF2F2' }}>
              {['Sale #', 'Client', 'Outstanding', 'Due Date', 'Overdue'].map(h =>
                <th key={h} className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-red-700">{h}</th>)}
            </tr></thead>
            <tbody>
              {items.map(i => (
                <tr key={i.saleId} className="border-t border-red-100 bg-red-50/40">
                  <td className="px-3 py-2 font-mono text-xs font-bold text-p-deep">{i.saleNumber}</td>
                  <td className="px-3 py-2">
                    <div className="text-xs font-semibold text-ink">{i.clientName}</div>
                    <div className="text-[10px] text-ink-4">{i.companyName}</div>
                  </td>
                  <td className="px-3 py-2 text-xs font-bold text-red-600">{fmtPKR(i.outstanding)}</td>
                  <td className="px-3 py-2 text-xs text-ink-4">{fmtDate(i.creditDueDate)}</td>
                  <td className="px-3 py-2"><span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700">{i.daysOverdue}d</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination {...page} onPage={p => { load(p).catch(() => { /* silent */ }); }} />
        <div className="text-right">
          <Link to="/direct-sales" onClick={() => setOpen(false)} className="text-xs font-semibold underline" style={{ color: 'var(--p)' }}>
            Go to Direct Sales →
          </Link>
        </div>
      </div>
    </Modal>
  );
}